import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ChartType,
  IChartFilter,
  SingleContractAnalyzerTypeEnum
} from 'app/charts-dashboard/interfaces/chart.interface';
import { map } from 'rxjs/operators';
import {
  ISingleContractAnalyzerChartPoint,
  IIntraDayOptionChartParameters,
  ILayoutList,
  IOptionInfoStrikes,
  IOptionInfoStrikesParameters
} from '../interfaces';
import { getHttpParams } from '@iv/shared/utils';
import {
  IChartPointsResponse,
  ChartParametersType,
  IHistoricalVolatilityChartParameters,
  IImpliedVolatilityChartParameters,
  IIvDifferenceChartParameters,
  IIvPremiumChartParameters,
  IIvxHistoryChartParameters,
  ISkewChartParameters,
  ISpotChartParameters,
  ITermStructureRollingChartParameters,
  ITotalOpenInterestVolumeChartParameters,
  AxisXHistorical,
  AxisXMaturity,
  PointXType,
  PointXTypes
} from 'app/charts-dashboard/interfaces/chart-http.interface';

@Injectable()
export class ChartsHttpService {

  constructor(private readonly http: HttpClient,) { }

  public getOptInfoExpirations(stockId: number): Observable<string[]> {
    return this.http.get<string[]>(`/options-monitor/charts/optionInfo/expirations?stockId=${stockId}`);
  }

  public getOptInfoStrikes(parameters: IOptionInfoStrikesParameters): Observable<IOptionInfoStrikes> {
    const params: HttpParams = getHttpParams(parameters as {});
    return this.http.get<IOptionInfoStrikes>(`/options-monitor/charts/optionInfo/strikes`, {observe: 'response', params})
      .pipe(map((response: HttpResponse<IOptionInfoStrikes>) => response.body));
  }

  // Ручка для получения точек SINGLE_CONTRACT_ANALYZER, скорее всего нужно будет перенести в option monitor api service
  public getContractView(parameters: IIntraDayOptionChartParameters): Observable<ISingleContractAnalyzerChartPoint[]> {
    const params: HttpParams = getHttpParams(parameters as {});
    return this.http.get<ISingleContractAnalyzerChartPoint[]>(`/options-monitor/charts/contractView`, {observe: 'response', params})
      .pipe(map((response: HttpResponse<ISingleContractAnalyzerChartPoint[]>) => {
        return response.body.map((item: ISingleContractAnalyzerChartPoint) => {
          Object.keys(item).forEach((key: string) => {
            if (key === SingleContractAnalyzerTypeEnum.IV) {
              item[key] = item[key] * 100;
            }
          });

          return item;
        });
      }));
  }

  public getAllCharts(): Observable<ChartType[]> {
    return this.http.get<ChartType[]>('/stock-charts/v2/charts/get/all');
  }

  public getAllParameters(): Observable<IChartFilter[]> {
    return this.http.get<IChartFilter[]>('/stock-charts/v2/charts/parameters');
  }

  public getLayouts(): Observable<ILayoutList> {
    return this.http.get<ILayoutList>('/iv-live/assets/charts-dashboard/charts-dashboard-layout-list.json?version=12');
  }

  public getSpotChart(params: ISpotChartParameters): Observable<IChartPointsResponse<AxisXHistorical>> {
    return this.mapResponse('spotChart', params);
  }

  public getIvxHistory(params: IIvxHistoryChartParameters): Observable<IChartPointsResponse<AxisXHistorical>> {
    return this.mapResponse('ivxHistory', params);
  }

  public getTotalOpenInterestVolume(params: ITotalOpenInterestVolumeChartParameters): Observable<IChartPointsResponse<AxisXHistorical>> {
    return this.mapResponse('totalOpenInterestVolume', params);
  }

  public getHistoricalVolatility(params: IHistoricalVolatilityChartParameters): Observable<IChartPointsResponse<AxisXHistorical>> {
    return this.mapResponse('historicalVolatility', params);
  }

  public getImpliedVolatility(params: IImpliedVolatilityChartParameters): Observable<IChartPointsResponse<AxisXHistorical>> {
    return this.mapResponse('impliedVolatility', params);
  }

  public getTermStructureRolling(params: ITermStructureRollingChartParameters): Observable<IChartPointsResponse<AxisXMaturity>> {
    return this.mapResponse('termStructureRolling', params);
  }

  public getSkewChart(params: ISkewChartParameters): Observable<IChartPointsResponse<AxisXMaturity>> {
    return this.mapResponse('skewChart', params);
  }

  public getIvPremiumChart(params: IIvPremiumChartParameters): Observable<IChartPointsResponse<AxisXHistorical>> {
    return this.mapResponse('ivPremiumChart', params);
  }

  public getIvDifference(params: IIvDifferenceChartParameters): Observable<IChartPointsResponse<AxisXHistorical>> {
    return this.mapResponse('ivDifference', params);
  }

  private mapResponse(url: string, parameters: ChartParametersType): Observable<IChartPointsResponse<PointXTypes>> {
    const params: HttpParams = getHttpParams(parameters as {});
    return this.http.get<IChartPointsResponse<PointXType>>(`/stock-charts/v2/charts/get/${url}`, {observe: 'response', params})
      .pipe(map((response: HttpResponse<IChartPointsResponse<PointXTypes>>) => response.body));
  }
}
